import FileDownloadIcon from "@mui/icons-material/FileDownload";
import React from "react";
import PropTypes from "prop-types";
import * as XLSX from "xlsx";
import {
  Button,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import TableViewIcon from "@mui/icons-material/TableView"; // Excel-like icon
import DescriptionIcon from "@mui/icons-material/Description"; // CSV-like icon
import ExcelJS from "exceljs";
const ExcelExportButton = ({ columns, rows, filename = "report" }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleOpenMenu = (e) => setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const STRING_COLUMN_KEYS = [
    // Basic info
    "aging",
    "AGING",
    "description",
    "gender",
    "marital_status",
    "religion",
    "specific_category",

    // Employment info
    "staff_id",
    "branch",
    "position",
    "employment_status",
    "leave_category",
    "reporting_level",
    "department",
    "grade",
    "staff_status",

    // Financial info
    "snnit_number",
    "tin_number",
    "employee_bank",
    "bank_account_number",
    "ghana_card_number",

    // Contact info
    "country",
    "postal_address",
    "residential_address",
    "city",
    "phone_number",
    "other_phone_number",
    "personal_email",
    "company_email",
    "hometown",
    "region",

    // Status / audit / flags
    "status",
    "removal_reason",
    "interdicted",
    "interdiction_reason",
    "employee_jumped_by",
    "employee_updated_by",
    "fortnight_updated_by",
  ];

  const extractCellValue = (cellValue) => {
    if (React.isValidElement(cellValue)) {
      if (cellValue.props.value !== undefined) return cellValue.props.value;

      if (cellValue.props.children) {
        return Array.isArray(cellValue.props.children)
          ? cellValue.props.children.join(" ")
          : cellValue.props.children;
      }
      return "";
    }
    return cellValue ?? "";
  };

  const headers = columns.map((col) => col.label);

  const extractedRows = rows.map((row) =>
    columns.map((col) => extractCellValue(row[col.id])),
  );

  const data = [headers, ...extractedRows];

  // ---------- CSV ----------
  const exportToCSV = () => {
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    const csv = XLSX.utils.sheet_to_csv(worksheet);

    const outName = filename.toLowerCase().endsWith(".csv")
      ? filename
      : `${filename}.csv`;

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.setAttribute("href", URL.createObjectURL(blob));
    link.setAttribute("download", outName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ---------- XLSX (auto-fit) ----------
  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Report");

    // ----- Add header -----
    worksheet.columns = columns.map((col) => ({
      header: col.label,
      key: col.id,
      width: 15,
    }));

    // Make header bold
    worksheet.getRow(1).font = { bold: true };

    // Freeze header row
    worksheet.views = [{ state: "frozen", ySplit: 1 }];

    // ----- Add data rows -----
    rows.forEach((row) => {
      const newRow = {};

      columns.forEach((col) => {
        let value = extractCellValue(row[col.id]);

        if (STRING_COLUMN_KEYS.includes(col.id)) {
          newRow[col.id] = String(value ?? "");
        } else {
          // Handle empty values FIRST
          if (value === "" || value === null || value === undefined) {
            newRow[col.id] = null; // ✅ this keeps Excel cell empty
          } else if (STRING_COLUMN_KEYS.includes(col.id)) {
            newRow[col.id] = String(value);
          } else {
            const cleaned = String(value)
              .replace(/,/g, "")
              .replace(/^(GHS|GH₵|₵|\$|£|€)/i, "");

            const num = Number(cleaned);

            newRow[col.id] = isNaN(num) ? value : num;
          }
        }
      });

      worksheet.addRow(newRow);
    });

    // ----- Format numeric columns -----
    worksheet.columns.forEach((column, index) => {
      const colId = columns[index].id;

      if (!STRING_COLUMN_KEYS.includes(colId)) {
        column.numFmt = "#,##0.00"; // commas + 2dp
      }

      // Auto-fit width
      let maxLength = column.header.length;
      column.eachCell({ includeEmpty: false }, (cell) => {
        const len = cell.value ? cell.value.toString().length : 10;
        if (len > maxLength) maxLength = len;
      });
      column.width = Math.min(maxLength + 2, 60);
    });

    const lower = filename.toLowerCase();
    const outName = lower.endsWith(".xlsx") ? filename : `${filename}.xlsx`;

    await workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = outName;
      link.click();
    });
  };

  const onPick = (type) => {
    handleCloseMenu();
    if (type === "excel") exportToExcel();
    if (type === "csv") exportToCSV();
  };

  return (
    <>
      <Tooltip title="Export table data">
        <Button
          style={{ fontSize: "12px" }}
          onClick={handleOpenMenu}
          endIcon={<FileDownloadIcon style={{ fontSize: "1rem" }} />}
        >
          Export
        </Button>
      </Tooltip>

      <Menu anchorEl={anchorEl} open={open} onClose={handleCloseMenu}>
        <MenuItem onClick={() => onPick("excel")}>
          <ListItemIcon>
            <TableViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Export to Excel (.xlsx)" />
        </MenuItem>

        <MenuItem onClick={() => onPick("csv")}>
          <ListItemIcon>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Export to CSV (.csv)" />
        </MenuItem>
      </Menu>
    </>
  );
};

ExcelExportButton.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  filename: PropTypes.string,
};

export default ExcelExportButton;
